import { OnDestroy, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ApplicationContextBoundComponent, PaymentCalculatorDialogComponent } from 'src/app/shared/components';
import { AdminRolePermissionService } from 'src/app/services/admin-role-permission.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { Constants } from 'src/app/services/constants';
import { HeaderMenuItem } from '../models/header-menu.model';
import { SUBMENU_LUCIDE_ICON_MAP, resolveSubmenuLucideIcon } from './header-menu.icons';
import { RecentAgent, RecentLead } from 'src/app/models';

@Component({
    selector: 'header-menu',
    templateUrl: './header-menu.component.html',
    styleUrls: ['./header-menu.component.scss'],
    standalone: false
})
export class HeaderMenuComponent extends ApplicationContextBoundComponent implements OnDestroy {

  @Input()
  set menu(menu: HeaderMenuItem[]) {
    this._rawMenu = menu;
    // If the company is smartfi, remove the quick pricer menu item
    if (this.applicationContext.isCompanySmartfi && this._rawMenu) {
      const myToolsMenuItem = this._rawMenu.find(item => item.text === 'My Tools');
      if (myToolsMenuItem) {
        myToolsMenuItem.children = myToolsMenuItem.children?.filter(item => item.text !== 'Quick Pricer');
      }
    }
    this.applyPermissionFilters();
  }

  get menu(): HeaderMenuItem[] {
    return this._menu;
  }

  menuItemStatuses: Map<string | number, boolean> = new Map<string | number, boolean>();

  tab: string = '';
  companyId: number;

  userHasAdminPermission: boolean = false;
  userHasSuperAdminPermission: boolean = false;
  userIsImpersonating: boolean = false;
  allowMortgageCallReports: boolean = false;
  branchesEnabled: boolean = false;
  pricingEnabled: boolean = false;
  externalCompanyEnabled: boolean = false;
  leadManagementEnabled: boolean = false;
  broadcastManagerEnabled: boolean = false;
  ocrEnabled: boolean = false;
  losEnabled: boolean = false;
  loanBeamEnabled: boolean = false;
  dialerEnabled: boolean = false;
  campaignsEnabled: boolean = false;
  areThereEnabledChannels: boolean = false;
  pipelineMetricsEnabled : boolean = false;
  pipelineWaterfallEnabled : boolean = false;
  urlaValidationEnabled: boolean = false;
  supportTicketingEnabled: boolean = false;
  compensationEnabled: boolean = false;

  @Output()
  newLeadClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  newAgentClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  newContactClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  newApplicationClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  viewLeadClicked: EventEmitter<RecentLead> = new EventEmitter<RecentLead>();

  @Output()
  viewAgentClicked: EventEmitter<RecentAgent> = new EventEmitter<RecentAgent>();

  @Output()
  menuItemClicked: EventEmitter<HeaderMenuItem> = new EventEmitter<HeaderMenuItem>();

  readonly allowedTabs = {
    ['Dashboard']: 'dashboardv2',
    ['Pipeline']: 'pipeline',
    ['Calendar']: 'calendar',
  }

  readonly topLevelLucideIconMap: Record<string, string> = {
    New: 'plus',
    Dashboard: 'layout-dashboard',
    Pipeline: 'kanban',
    Calendar: 'calendar-days',
    MyLists: 'list',
    MyTools: 'wrench',
    Reporting: 'bar-chart-3',
    Admin: 'settings',
    RecentlyVisited: 'history',
  };

  /** See header-menu.icons.ts — kept as a field so the template contract is unchanged. */
  readonly submenuLucideIconMap: Record<string, string> = SUBMENU_LUCIDE_ICON_MAP;

  getLucideIcon = (item: HeaderMenuItem): string | null => {
    return item?.id ? (this.topLevelLucideIconMap[item.id] ?? null) : null;
  }


  getSubmenuLucideIcon = (item: HeaderMenuItem): string | null => resolveSubmenuLucideIcon(item);

  private _rawMenu: HeaderMenuItem[] = [];
  private _menu: HeaderMenuItem[] = [];
  private _configChangedSubscription: Subscription;

  constructor(
    private readonly injector: Injector,
    private readonly adminRolePermissionService: AdminRolePermissionService,
    private readonly configurationService: ConfigurationService,
    private readonly modalService: NgbModal,
  ) {
    super(injector);

    this.userHasAdminPermission = this.applicationContext.userPermissions.admin;
    this.userHasSuperAdminPermission = this.applicationContext.userPermissions.superAdmin;
    this.allowMortgageCallReports = this.applicationContext.userPermissions.allowMortgageCallReports;
    this.leadManagementEnabled = this.applicationContext.userPermissions.leadManagementEnabled;
    this.userIsImpersonating = this.userHasSuperAdminPermission && this.applicationContext.currentlyLoggedInUserProfile.userProfile.companyId != 1;
    this.companyId = this.applicationContext.currentlyLoggedInUserProfile.userProfile.companyId;
    this.broadcastManagerEnabled = this.applicationContext.userPermissions.broadcastManagerEnabled;

    this.branchesEnabled = this.applicationContext.userPermissions.branchesEnabled;
    this.pricingEnabled = this.applicationContext.userPermissions.pricingEnabled;
    this.ocrEnabled = this.applicationContext.userPermissions.ocrEnabled;
    this.losEnabled = this.applicationContext.userPermissions.losEnabled;
    this.loanBeamEnabled = this.applicationContext.userPermissions.loanBeamEnabled;
    this.dialerEnabled = this.applicationContext.userPermissions.dialerEnabled;
    this.campaignsEnabled = this.applicationContext.userPermissions.campaignsEnabled;
    this.pipelineMetricsEnabled = this.applicationContext.userPermissions.allowPipelineMetrics;
    this.pipelineWaterfallEnabled = this.applicationContext.userPermissions.allowPipelineWaterfall;
    this.areThereEnabledChannels = !!this.applicationContext.userPermissions.enabledChannels && this.applicationContext.userPermissions.enabledChannels !== 'Disabled';
    this.externalCompanyEnabled = this.applicationContext.globalConfig.company.find(x => x.companyId == this.applicationContext.currentlyLoggedInUserProfile.userProfile.companyId)?.externalCompanyEnabled ?? false;
    this.compensationEnabled = this.applicationContext.userPermissions.compensationEnabled;
    this.urlaValidationEnabled = true;
    this.configurationService.getCompanyConfiguration('SupportTicketingEnabled').subscribe({
      next: config => {
        this.supportTicketingEnabled = config?.value === 1;
        this.applyPermissionFilters();
      },
      error: () => {
        this.supportTicketingEnabled = false;
        this.applyPermissionFilters();
      }
    });

    // Re-fetch when company configuration is changed (e.g., after saving Application Settings)
    this._configChangedSubscription = this.configurationService.companyConfigChanged$.subscribe(key => {
      if (key === 'SupportTicketingEnabled') {
        this.configurationService.getCompanyConfiguration('SupportTicketingEnabled').subscribe({
          next: config => {
            this.supportTicketingEnabled = config?.value === 1;
            this.applyPermissionFilters();
          },
          error: () => {
            this.supportTicketingEnabled = false;
            this.applyPermissionFilters();
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._configChangedSubscription?.unsubscribe();
  }

  getRouterLink = (menuItem: HeaderMenuItem): string => {
    return !menuItem.event ? menuItem.link : undefined;
  }

  onMenuToggled = (id: string | number, menuItem: HeaderMenuItem, level?: number) => {
    const currentStatus = this.menuItemStatuses.get(id);
    if (!currentStatus) {
      this.menuItemStatuses.set(id, true);
    } else {
      this.menuItemStatuses.set(id, false);
    }

    this.menuItemClicked.emit(menuItem);

    this.triggerEvent(menuItem);
  }

  /** Collapse every expanded submenu. Called by the parent admin-header when
   *  the hamburger drawer closes so the next open starts in a clean state
   *  instead of restoring whichever sections the user had previously expanded. */
  collapseAllSubmenus = () => {
    this.menuItemStatuses.clear();
  }

  openNewLeadDrawer = () => {
    this.newLeadClicked.emit();
  }

  openNewAgentDrawer = () => {
    this.newAgentClicked.emit();
  }

  openNewContactDrawer = () => {
    this.newContactClicked.emit();
  }

  openNewApplicationDrawer = () => {
    this.newApplicationClicked.emit();
  }

  openPaymentCalculator = () => {
    this.modalService.open(PaymentCalculatorDialogComponent, Constants.modalOptions.large);
  }

  openLeadDrawer = (menuItem: HeaderMenuItem) => {
    this.viewLeadClicked.emit(menuItem.data.lead);
  }

  openAgentDrawer = (menuItem: HeaderMenuItem) => {
    this.viewAgentClicked.emit(menuItem.data.agent);
  }

  private triggerEvent = (menuItem: HeaderMenuItem) => {
    if (menuItem.event && this[menuItem.event]) {
      this[menuItem.event](menuItem);
    }
  }


  private applyPermissionFilters(): void {
    // Deep clone to avoid mutating the raw menu, so re-filtering works correctly
    const cloned: HeaderMenuItem[] = JSON.parse(JSON.stringify(this._rawMenu || []));
    this._menu = this.filterMenuByPermissions(cloned);
  }

  private filterMenuByPermissions = (menuItems: HeaderMenuItem[]): HeaderMenuItem[] => {
    return menuItems.filter(item => {
      // Check basic admin permission
      if (item.requireAdminPermission && !this.userHasAdminPermission) {
        return false;
      }

      // Check super admin permission
      if (item.requireSuperAdminPermission && !this.userHasSuperAdminPermission) {
        return false;
      }

      // Check AdminRole claim requirement
      if (item.requiredAdminClaim && !this.adminRolePermissionService.hasAdminRolePermission(item.requiredAdminClaim, this.applicationContext.userPermissions)) {
        return false;
      }

      // Check other permission requirements
      if (item.requireEnabledPricingPermission && !this.pricingEnabled) {
        return false;
      }

      if (item.requireCallReportPermission && !this.allowMortgageCallReports) {
        return false;
      }

      if (item.requireExternalCompanyEnabled && !this.externalCompanyEnabled) {
        return false;
      }

      if (item.requiredEnabledLeadManagementPermission && !this.leadManagementEnabled) {
        return false;
      }

      if (item.requireBroadcastManagerAccessPermission && !this.broadcastManagerEnabled) {
        return false;
      }

      if (item.requireEnabledOcrPermission && !this.ocrEnabled) {
        return false;
      }

      if (item.requireEnabledLosPermission && !this.losEnabled) {
        return false;
      }

      if (item.requireEnabledLoanBeamPermission && !this.loanBeamEnabled) {
        return false;
      }

      if (item.requireEnabledChannelsPermission && !this.areThereEnabledChannels) {
        return false;
      }

      if (item.requireEnabledDialerPermission && !this.dialerEnabled) {
        return false;
      }

      if (item.requireEnabledCampaignsPermission && !this.campaignsEnabled) {
        return false;
      }

      if (item.requirePipelineMetricsPermission && !this.pipelineMetricsEnabled) {
        return false;
      }

      if (item.requirePipelineWaterfallPermission && !this.pipelineWaterfallEnabled) {
        return false;
      }

      if (item.requireEnabledBranchesPermission && !this.branchesEnabled) {
        return false;
      }

      if (item.restrictedWhileImpersonating && this.userIsImpersonating) {
        return false;
      }

      if (item.requireUrlaValidationPermission && !this.urlaValidationEnabled) {
        return false;
      }

      if (item.requireSupportTicketingEnabled && !this.supportTicketingEnabled) {
        return false;
      }

      if (item.requireEnabledCompensationPermission && !this.compensationEnabled) {
        return false;
      }

      if (item.incompleteFeature && this.companyId != 2) {
        return false;
      }

      // Recursively filter children
      if (item.children?.length) {
        item.children = this.filterMenuByPermissions(item.children);
        // If all children are filtered out, hide the parent menu item
        if (item.children.length === 0) {
          return false;
        }
      }

      return true;
    });
  }
}
